import { Document } from '@/models/Document';
import { DocumentProcessingJob } from '@/models/DocumentProcessingJob';
import { apiClient } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

export const createDocuments = async (requestBody: {
  /** Required — documents must always be uploaded to an existing property. */
  propertyId: string;
  documents: { filename: string; fileSize: number; mimeType: string; s3Key: string }[];
  source?: 'seller' | 'buyer';
  uploadBatchId?: string;
  expectedDocumentCount?: number;
}) => {
  if (!requestBody.propertyId) {
    throw new Error('createDocuments: propertyId is required.');
  }
  const url = '/documents';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<Document[]>(url, requestBody);

  return response.data;
};

export const createDocumentUploadBatch = async (requestBody: {
  propertyId: string;
  expectedDocumentCount: number;
}) => {
  const response = await apiClient.post<DocumentProcessingJob>(
    '/document-upload-batches',
    requestBody,
  );
  return response.data;
};

export const recordDocumentUploadBatchFailure = async (
  batchId: string,
  requestBody: {
    fileKey: string;
    s3Key?: string;
    stage: 'transfer' | 'registration';
  },
) => {
  const response = await apiClient.post<{ registered?: boolean }>(
    `/document-upload-batches/${batchId}/failures`,
    requestBody,
  );
  return response.data;
};

export interface ConfirmAddressOverrideResponse {
  message: string;
  documentId: string;
  propertyId: string;
  executionArn?: string;
}

/**
 * Resumes the AI pipeline after the classifier flagged an address mismatch.
 * Re-runs the state machine for the same document with the address check
 * skipped on the second pass.
 */
export const confirmAddressOverride = async (documentId: string) => {
  const url = `/documents/${documentId}/confirm-address-override`;
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<ConfirmAddressOverrideResponse>(url);
  return response.data;
};

export interface RetryDocumentProcessingResponse {
  message: string;
  documentId: string;
  executionArn?: string;
  retryAttemptCount?: number;
}

/**
 * Re-runs the AI pipeline from scratch for a document whose processing landed
 * on Incomplete — a Pulse-dependent step exhausted its retry budget, most
 * likely because Pulse itself was unavailable. Admin/superadmin only.
 */
export const retryDocumentProcessing = async (documentId: string) => {
  const url = `/documents/${documentId}/retry-processing`;
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<RetryDocumentProcessingResponse>(url);
  return response.data;
};

export interface ReportDocumentIssueResponse {
  message: string;
  documentId: string;
  sentryFeedbackEventId?: string;
}

/**
 * Files a Sentry Feedback item with full document context for a document
 * still Incomplete after one or more manual retries — the escalation path
 * beyond "just retry again". Admin/superadmin only.
 */
export const reportDocumentIssue = async (documentId: string, comments?: string) => {
  const url = `/documents/${documentId}/report-issue`;
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<ReportDocumentIssueResponse>(url, { comments });
  return response.data;
};

export interface CancelPendingUploadResponse {
  message: string;
  documentId: string;
  deletedRecordCount: number;
}

/**
 * Discards a NEEDS_CONFIRMATION upload entirely — removes the S3 object,
 * every linked DocumentRecord, and the Document row. Used when the user
 * picks "Cancel upload" on the address-mismatch modal.
 */
export const cancelPendingUpload = async (documentId: string) => {
  const url = `/documents/${documentId}/cancel-pending-upload`;
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<CancelPendingUploadResponse>(url);
  return response.data;
};
